import React, { useState, useEffect } from 'react';
import { getExerciseHistory } from '../services/historyService';
import { colors as theme } from '../theme';

const colors = {
  bg: theme.bg,
  surface1: theme.bg,
  surface2: theme.surface1,
  surface3: theme.surface3,
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.12)',
  text: theme.text,
  textSecondary: '#EBEBF5',
  textMuted: theme.textMuted,
  accent: theme.gold,
  success: theme.success,
  error: theme.danger,
};

const typography = {
  h2: { fontSize: '20px', fontWeight: '700', lineHeight: '1.3' },
  h3: { fontSize: '18px', fontWeight: '600', lineHeight: '1.4' },
  body: { fontSize: '15px', fontWeight: '400', lineHeight: '1.6' },
  small: { fontSize: '13px', fontWeight: '500', lineHeight: '1.5' },
  tiny: { fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px' },
};

const ExerciseHistoryModal = ({ exerciseId, exerciseName, userId, isAssisted = false, isBodyweight = false, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupBy, setGroupBy] = useState('session'); // session or set

  useEffect(() => {
    if (exerciseId && userId) {
      loadHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseId, userId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getExerciseHistory(userId, exerciseId, 100, exerciseName);
      setHistory(data || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading exercise history:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatWeight = (weight, reps) => {
    if (isBodyweight) return `BW × ${reps ?? '?'} reps`;
    if (isAssisted) return `Assisted -${weight ?? '?'}kg × ${reps ?? '?'} reps`;
    return `${weight ?? '?'}kg × ${reps ?? '?'} reps`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const groupHistoryBySessions = () => {
    const sessions = {};
    history.forEach(log => {
      if (!log.session_id || !log.session_date) return;
      const sessionKey = `${log.session_id}-${log.session_date}`;
      if (!sessions[sessionKey]) {
        sessions[sessionKey] = {
          date: log.session_date,
          program: log.program_name,
          sets: [],
        };
      }
      sessions[sessionKey].sets.push({
        setNumber: log.set_number,
        weight: log.weight,
        reps: log.reps,
        rir: log.rir,
      });
    });

    return Object.values(sessions).sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  };

  const calculateBest = (sets) => {
    if (!sets || sets.length === 0) return null;
    if (isAssisted) {
      // For assisted: best = lowest assistance weight (hardest)
      return sets.reduce((best, current) =>
        (current.weight ?? Infinity) < (best.weight ?? Infinity) ? current : best
      , sets[0]);
    }
    return sets.reduce((best, current) => {
      const currentVolume = (current.weight || 0) * (current.reps || 0);
      const bestVolume = (best.weight || 0) * (best.reps || 0);
      return currentVolume > bestVolume ? current : best;
    }, sets[0]);
  };

  const calculatePR = () => {
    if (history.length === 0) return null;
    if (isAssisted) {
      // For assisted: PR = lowest assistance weight ever used (hardest)
      return history.reduce((best, current) =>
        (current.weight ?? Infinity) < (best.weight ?? Infinity) ? current : best
      , history[0]);
    }
    return history.reduce((best, current) => {
      const currentVolume = (current.weight || 0) * (current.reps || 0);
      const bestVolume = (best.weight || 0) * (best.reps || 0);
      return currentVolume > bestVolume ? current : best;
    }, history[0]);
  };

  const sessions = groupHistoryBySessions();
  const pr = calculatePR();

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={{ ...typography.h2, color: colors.text, margin: 0 }}>
              {exerciseName}
            </h2>
            <p style={{ ...typography.small, color: colors.textMuted, margin: '4px 0 0' }}>
              Complete Exercise History
            </p>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>
            ✕
          </button>
        </div>

        {/* PR Card */}
        {pr && (
          <div style={styles.prCard}>
            <span style={{ fontSize: '32px' }}>🏆</span>
            <div>
              <span style={{ ...typography.tiny, color: colors.textMuted }}>
                PERSONAL RECORD
              </span>
              <span style={{ ...typography.h3, color: colors.accent, display: 'block', marginTop: '4px' }}>
                {formatWeight(pr.weight, pr.reps)}
              </span>
              <span style={{ ...typography.tiny, color: colors.textMuted, display: 'block', marginTop: '4px' }}>
                {formatDate(pr.session_date)} • {pr.program_name}
              </span>
            </div>
          </div>
        )}

        {/* View Toggle */}
        <div style={styles.viewToggle}>
          <button
            onClick={() => setGroupBy('session')}
            style={{
              ...styles.toggleBtn,
              ...(groupBy === 'session' ? styles.toggleBtnActive : {}),
            }}
          >
            By Session
          </button>
          <button
            onClick={() => setGroupBy('set')}
            style={{
              ...styles.toggleBtn,
              ...(groupBy === 'set' ? styles.toggleBtnActive : {}),
            }}
          >
            All Sets
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {loading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner} />
              <p style={{ ...typography.small, color: colors.textMuted, marginTop: '12px' }}>
                Loading history...
              </p>
            </div>
          ) : error ? (
            <div style={styles.errorContainer}>
              <p style={{ ...typography.body, color: colors.error }}>
                {error}
              </p>
            </div>
          ) : history.length === 0 ? (
            <div style={styles.emptyContainer}>
              <span style={{ fontSize: '48px' }}>📋</span>
              <p style={{ ...typography.body, color: colors.textMuted, marginTop: '16px' }}>
                No history available for this exercise
              </p>
            </div>
          ) : groupBy === 'session' ? (
            <div style={styles.sessionList}>
              {sessions.map((session, index) => {
                const bestSet = calculateBest(session.sets);
                return (
                  <div key={index} style={styles.sessionCard}>
                    <div style={styles.sessionHeader}>
                      <div>
                        <span style={{ ...typography.body, color: colors.text, fontWeight: '600' }}>
                          {formatDate(session.date)}
                        </span>
                        <span style={{ ...typography.small, color: colors.accent, display: 'block', marginTop: '4px' }}>
                          {session.program}
                        </span>
                      </div>
                      {bestSet && (
                        <div style={styles.bestSetBadge}>
                          <span style={{ ...typography.tiny, color: colors.textMuted }}>BEST</span>
                          <span style={{ ...typography.small, color: colors.text, fontWeight: '600' }}>
                            {formatWeight(bestSet.weight, bestSet.reps)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div style={styles.setsGrid}>
                      {session.sets.sort((a, b) => a.setNumber - b.setNumber).map((set, setIndex) => (
                        <div key={setIndex} style={styles.setItem}>
                          <span style={{ ...typography.tiny, color: colors.textMuted }}>
                            Set {set.setNumber}
                          </span>
                          <span style={{ ...typography.small, color: colors.text }}>
                            {formatWeight(set.weight, set.reps)}
                          </span>
                          {set.rir !== null && (
                            <span style={{ ...typography.tiny, color: colors.textMuted }}>
                              RIR {set.rir}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={styles.setList}>
              {history.map((log, index) => (
                <div key={index} style={styles.setRow}>
                  <div>
                    <span style={{ ...typography.small, color: colors.text, fontWeight: '600' }}>
                      {formatWeight(log.weight, log.reps)}
                    </span>
                    {log.rir !== null && (
                      <span style={{ ...typography.tiny, color: colors.textMuted, marginLeft: '8px' }}>
                        RIR {log.rir}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                    <span style={{ ...typography.tiny, color: colors.textMuted }}>
                      {formatDate(log.session_date)}
                    </span>
                    <span style={{ ...typography.tiny, color: colors.accent }}>
                      {log.program_name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Stats */}
        {history.length > 0 && (
          <div style={styles.footer}>
            <div style={styles.footerStat}>
              <span style={{ ...typography.tiny, color: colors.textMuted }}>TOTAL SESSIONS</span>
              <span style={{ ...typography.body, color: colors.text, fontWeight: '600' }}>
                {sessions.length}
              </span>
            </div>
            <div style={styles.footerStat}>
              <span style={{ ...typography.tiny, color: colors.textMuted }}>TOTAL SETS</span>
              <span style={{ ...typography.body, color: colors.text, fontWeight: '600' }}>
                {history.length}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    backgroundColor: colors.surface1,
    borderRadius: '20px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${colors.border}`,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '24px 24px 16px',
    borderBottom: `1px solid ${colors.border}`,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: colors.textMuted,
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px 8px',
    marginTop: '-4px',
  },
  prCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: '20px 24px',
    padding: '20px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.borderLight}`,
  },
  viewToggle: {
    display: 'flex',
    gap: '8px',
    padding: '0 24px',
    marginBottom: '16px',
  },
  toggleBtn: {
    flex: 1,
    padding: '10px 16px',
    backgroundColor: colors.surface2,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    color: colors.textMuted,
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  toggleBtnActive: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
    color: colors.bg,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 24px 20px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 20px',
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: `3px solid ${colors.surface2}`,
    borderTop: `3px solid ${colors.accent}`,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  errorContainer: {
    padding: '48px 20px',
    textAlign: 'center',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 20px',
    textAlign: 'center',
  },
  sessionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sessionCard: {
    padding: '16px',
    backgroundColor: colors.surface2,
    borderRadius: '14px',
    border: `1px solid ${colors.border}`,
  },
  sessionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    gap: '16px',
  },
  bestSetBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '2px',
    padding: '8px 12px',
    backgroundColor: colors.surface3,
    borderRadius: '8px',
  },
  setsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '8px',
  },
  setItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '10px',
    backgroundColor: colors.surface3,
    borderRadius: '8px',
  },
  setList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  setRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: colors.surface2,
    borderRadius: '8px',
    border: `1px solid ${colors.border}`,
  },
  footer: {
    display: 'flex',
    gap: '24px',
    padding: '16px 24px',
    borderTop: `1px solid ${colors.border}`,
  },
  footerStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
};

export default ExerciseHistoryModal;
